import React, { useCallback, useMemo, useRef, useState } from 'react';

import SnackBarProvider, { SnackBarContext } from 'pages/components/SnackBarProvider';
import useHistoryOfChanges from 'pages/item/v2/hooks/use-history-of-changes';

import HistoryOfChangesContext from '../../contexts/history-of-changes-context';

interface Props {
    id: string;
}

const HistoryOfChangesProvider: React.FC<Props> = ({ id, children }) => {
    const snackBarRef = useRef<SnackBarContext>(null);

    const [version, setVersion] = useState<number>(NaN);
    const { data, isLoading, isFetched, refetch } = useHistoryOfChanges({ id }, { enabled: false });
    const loadingPromiseRef = useRef<Promise<void> | null>(null);
    const load = useCallback(async () => {
        if (isFetched) return;
        if (loadingPromiseRef.current) return loadingPromiseRef.current;

        loadingPromiseRef.current = refetch()
            .then(([head]) => setVersion((version) => head?.version ?? version))
            .catch((error) =>
                snackBarRef.current?.openNotification({
                    level: 'error',
                    message: error.message || 'Something went wrong'
                })
            )
            .finally(() => {
                loadingPromiseRef.current = null;
            });

        return loadingPromiseRef.current;
    }, [isFetched, refetch]);

    const revision = useMemo(() => data.find((item) => item.version === version) ?? null, [data, version]);

    const contextValue = useMemo(() => ({ data, revision, version, setVersion, isLoading, load }), [
        data,
        isLoading,
        load,
        revision,
        version
    ]);

    return (
        <HistoryOfChangesContext.Provider value={contextValue}>
            <SnackBarProvider ref={snackBarRef}>{children}</SnackBarProvider>
        </HistoryOfChangesContext.Provider>
    );
};

export default HistoryOfChangesProvider;
